import axios from "axios";
import React, { useEffect, useState, useMemo, useCallback } from "react";
import { useParams } from "react-router-dom";
import {
  Button,
  Card,
  CardContent,
  Typography,
  Alert,
  Snackbar,
  CircularProgress,
  Paper,
  Box,
  Grid,
  Divider,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
} from "@mui/material";
import ArrowUpwardIcon from "@mui/icons-material/ArrowUpward";
import ArrowDownwardIcon from "@mui/icons-material/ArrowDownward";
import RemoveIcon from "@mui/icons-material/Remove";
import WarningIcon from "@mui/icons-material/Warning";
import { MaterialReactTable } from "material-react-table";
import styled from "styled-components";
import ReactECharts from "echarts-for-react";
import TPC_URL from "../../../utils/TPC_URL";
import StockInModal from "./StockInModal";
import StockOutModal from "./StockOutModal";
import dayjs from "dayjs";

const Wrapper = styled.div`
  display: flex;
  flex-direction: column;
  width: 100%;
  height: calc(100vh - 65px);
  background-color: var(--mui-palette-background-default);
  overflow: hidden;
  box-sizing: border-box;
`;

const FrameContainer = styled.div`
  display: flex;
  flex-direction: column;
  width: 100%;
  height: 100%;
  flex: 1;
  overflow: hidden;
  background-color: var(--mui-palette-background-paper);
  box-shadow: var(--mui-shadows-2);
`;

const ToolbarContainer = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  padding: 16px 24px;
  border-bottom: 1px solid var(--mui-palette-divider);
`;

const ToolbarLeft = styled.div`
  flex: 1;
  display: flex;
  align-items: center;
  gap: 1.5rem;
`;

const ToolbarRight = styled.div`
  flex: 1;
  display: flex;
  justify-content: flex-end;
  gap: 1rem;
`;

const DashboardContainer = styled.div`
  flex: 1;
  display: flex;
  flex-direction: column;
  overflow: auto;
  padding: 16px;
`;

const StockChangeIndicator = styled.div`
  display: flex;
  align-items: center;
  justify-content: center;
  margin-top: 8px;
  font-size: 0.875rem;
  font-weight: 500;
`;

const StatCard = styled(Card)`
  height: 100%;
  display: flex;
  flex-direction: column;
`;

const StatCardContent = styled(CardContent)`
  flex: 1;
  display: flex;
  flex-direction: column;
  padding: 16px !important;
`;

const ChartContainer = styled(Paper)`
  padding: 16px;
  height: 500px;
  display: flex;
  flex-direction: column;
  width: 100%;
`;

const ChartHeader = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: 8px;
`;

// ✅ 3개 차트를 위한 컨테이너 (1:1:1 비율)
const TripleChartContainer = styled(Box)`
  display: flex;
  flex-direction: row;
  height: 100%;
  width: 100%;
  gap: 16px;
`;

const ChartSection = styled(Box)`
  flex: 1;
  height: 100%;
  display: flex;
  flex-direction: column;
`;

function Stock() {
  const { group_code, project_code } = useParams();
  const [, setSystemDate] = useState("");
  const [totalStockData, setTotalStockData] = useState([]);
  const [itemInfoData, setItemInfoData] = useState([]);
  const [loading, setLoading] = useState(false);
  const [snackbar, setSnackbar] = useState({
    open: false,
    message: "",
    severity: "success",
  });
  const [timeRange, setTimeRange] = useState("week");
  const [updateStatus, setUpdateStatus] = useState(false);
  const [lowStockItems, setLowStockItems] = useState([]);
  const [inStockTrendData, setInStockTrendData] = useState([]);
  const [outStockTrendData, setOutStockTrendData] = useState([]);
  const [stockQuantityTrendData, setStockQuantityTrendData] = useState([]);
  const [trendDataLoading, setTrendDataLoading] = useState(false);
  const [pageIndex, setPageIndex] = useState(0);
  const [pageSize, setPageSize] = useState(10);
  const [openInModal, setOpenInModal] = useState(false);
  const [openOutModal, setOpenOutModal] = useState(false);
  const [itemTypeFilter, setItemTypeFilter] = useState("all");

  const openSnackbar = (message, severity = "success") => {
    setSnackbar({ open: true, message, severity });
  };

  const closeSnackbar = () => {
    setSnackbar((prev) => ({ ...prev, open: false }));
  };

  const fetchTrendData = useCallback(async () => {
    setTrendDataLoading(true);
    try {
      let duration;
      switch (timeRange) {
        case "week":
          duration = 6;
          break;
        case "month":
          duration = 29;
          break;
        case "quarter":
          duration = 89;
          break;
        default:
          duration = 6;
      }
      const inStockResponse = await axios.get(
        TPC_URL +
          `/api/stock/getTransition?group_code=${group_code}&project_code=${project_code}&direction=in&duration=${duration}`
      );
      const outStockResponse = await axios.get(
        TPC_URL +
          `/api/stock/getTransition?group_code=${group_code}&project_code=${project_code}&direction=out&duration=${duration}`
      );
      const stockQuantityResponse = await axios.get(
        TPC_URL +
          `/api/stock/getTransition?group_code=${group_code}&project_code=${project_code}&direction=stock&duration=${duration}`
      );
      setInStockTrendData(inStockResponse.data || []);
      setOutStockTrendData(outStockResponse.data || []);
      setStockQuantityTrendData(stockQuantityResponse.data || []);
    } catch (error) {
      console.error("추이 데이터 불러오기 실패:", error);
      openSnackbar("추이 데이터를 불러오지 못했습니다.", "error");
    } finally {
      setTrendDataLoading(false);
    }
  }, [timeRange, group_code, project_code]);

  useEffect(() => {
    fetchTrendData();
  }, [fetchTrendData]);

  const organizeTrendData = (
    data,
    direction = "in",
    allowedItemCodes = null,
    itemCodeToGroupMap = null
  ) => {
    const filteredData = allowedItemCodes
      ? data.filter((item) =>
          allowedItemCodes.has(item.item_code || item.item_name)
        )
      : data;

    const groupKeysSet = new Set();
    const dateGroups = {};
    const allDates = [];
    filteredData.forEach((item) => {
      const groupKey =
        (itemCodeToGroupMap &&
          itemCodeToGroupMap.get(item.item_code || item.item_name)) ||
        item.item_code;
      groupKeysSet.add(groupKey);

      if (!dateGroups[item.snapshot_date]) {
        dateGroups[item.snapshot_date] = {};
        allDates.push(item.snapshot_date);
      }
      let valueField;
      if (direction === "in") valueField = "daily_stock_in";
      else if (direction === "out") valueField = "daily_stock_out";
      else if (direction === "stock") valueField = "total_stock_quantity";

      if (!dateGroups[item.snapshot_date][groupKey]) {
        dateGroups[item.snapshot_date][groupKey] = 0;
      }
      dateGroups[item.snapshot_date][groupKey] += parseInt(
        item[valueField] || 0,
        10
      );
    });
    allDates.sort();
    const groupKeys = Array.from(groupKeysSet);
    const series = groupKeys.map((groupKey) => ({
      name: groupKey,
      type: "line",
      data: allDates.map((date) => dateGroups[date][groupKey] || 0),
    }));
    return { dates: allDates, series };
  };

  useEffect(() => {
    async function fetchData(isRetry = false) {
      setLoading(true);
      try {
        const snapshotRes = await axios.get(
          TPC_URL +
            `/api/stock/mountSnapshotCheck?group_code=${group_code}&project_code=${project_code}`
        );

        if (snapshotRes?.data?.message === 1 && !isRetry) {
          await fetchData(true);
          return;
        }
        const stockSummaryRes = await axios.get(
          TPC_URL +
            `/api/stock/getSummary?group_code=${group_code}&project_code=${project_code}`
        );
        const progressRes = await axios.get(
          TPC_URL +
            `/api/instruction/getInstructionProgress?group_code=${group_code}&project_code=${project_code}`
        );
        const itemInfoRes = await axios.get(
          TPC_URL +
            `/api/item/getAllItems?group_code=${group_code}&project_code=${project_code}`
        );

        const itemInfo = Array.isArray(itemInfoRes.data)
          ? itemInfoRes.data
          : [];
        setItemInfoData(itemInfo);

        const stockData = itemInfo.map((content) => {
          const matchedInfo = stockSummaryRes.data.find(
            (el) => el.item_code === content.item_code
          );
          const matchedProgress = progressRes.data.find(
            (el) => el.item_id === content.item_id
          );

          const expectedStock = matchedProgress
            ? Number(matchedProgress.remaining_qty)
            : 0;
          
          const totalStockQuantity = matchedInfo
            ? Number(matchedInfo.total_stock_quantity)
            : 0;

          if (matchedInfo) {
            const min_stock_quantity = content.safety_stock;
            return {
              ...matchedInfo,
              total_stock_quantity: totalStockQuantity,
              min_stock_quantity,
              expected_stock: expectedStock,
              item_id: content.item_id,
              material_grouping: content.material_grouping,
              sub_code: content.sub_code,
              spec: content.spec,
              car_type: content.car_type,
              item_type: content.item_type,
            };
          } else {
            return {
              changeQty: 0,
              min_stock_quantity: content.safety_stock || 0,
              total_stock_quantity: 0,
              yesterday_quantity: 0,
              item_code: content.item_code,
              expected_stock: expectedStock,
              item_id: content.item_id,
              material_grouping: content.material_grouping,
              sub_code: content.sub_code,
              spec: content.spec,
              car_type: content.car_type,
              item_type: content.item_type,
            };
          }
        });

        const moment = dayjs();
        setSystemDate(moment.format("YYYY-MM-DD"));
        setTotalStockData(stockData);
      } catch (e) {
        console.log(e);
        openSnackbar("자료를 불러오지 못했습니다.", "error");
      } finally {
        setLoading(false);
      }
    }
    fetchData();
  }, [group_code, project_code, updateStatus]);

  const formatQuantity = (quantity) => {
    return quantity.toString().replace(/\B(?=(\d{3})+(?!\d))/g, ",") + " EA";
  };

  const filterByItemType = useCallback(
    (item) => {
      if (itemTypeFilter === "finished") return item.item_type === 1;
      if (itemTypeFilter === "semi") return item.item_type === 0;
      return true;
    },
    [itemTypeFilter]
  );

  const filteredStockData = useMemo(
    () => totalStockData.filter(filterByItemType),
    [totalStockData, filterByItemType]
  );

  useEffect(() => {
    const lowStock = filteredStockData.filter(
      (item) =>
        Number(item.total_stock_quantity) + Number(item.expected_stock) <
        Number(item.min_stock_quantity)
    );
    setLowStockItems(lowStock);
  }, [filteredStockData]);

  const renderChangeIndicator = (changeQty) => {
    const changeValue = Number(changeQty);
    let color, icon, formattedChange;
    if (changeValue > 0) {
      color = "#1976D2";
      icon = (
        <ArrowUpwardIcon fontSize="small" style={{ marginRight: "4px" }} />
      );
      formattedChange = `+${changeValue.toLocaleString()} EA`;
    } else if (changeValue < 0) {
      color = "#f44336";
      icon = (
        <ArrowDownwardIcon fontSize="small" style={{ marginRight: "4px" }} />
      );
      formattedChange = `${changeValue.toLocaleString()} EA`;
    } else {
      color = "#555555";
      icon = <RemoveIcon fontSize="small" style={{ marginRight: "4px" }} />;
      formattedChange = "0 EA";
    }
    return (
      <StockChangeIndicator style={{ color }}>
        {icon}
        {formattedChange}
      </StockChangeIndicator>
    );
  };

  useEffect(() => {
    setPageIndex(0);
  }, [filteredStockData]);

  const calculateTotalStock = () => {
    let total = 0;
    filteredStockData.forEach((item) => {
      total += Number(item.total_stock_quantity);
    });
    return total;
  };

  const handleTimeRangeChange = (event) => {
    setTimeRange(event.target.value);
  };

  const handleItemTypeFilterChange = (event) => {
    setItemTypeFilter(event.target.value);
  };

  const getAllowedItemCodes = () => {
    if (itemTypeFilter === "finished") {
      return new Set(
        itemInfoData
          .filter((item) => item.item_type === 1)
          .map((item) => item.item_code || item.item_name)
      );
    }
    if (itemTypeFilter === "semi") {
      return new Set(
        itemInfoData
          .filter((item) => item.item_type === 0)
          .map((item) => item.item_code || item.item_name)
      );
    }
    return null;
  };

  const itemCodeToGroupMap = useMemo(() => {
    const map = new Map();
    itemInfoData.forEach((item) => {
      const codeKey = item.item_code || item.item_name;
      const group = item.material_grouping || codeKey;
      map.set(codeKey, group);
    });
    return map;
  }, [itemInfoData]);

  const columns = [
    {
      accessorKey: "sub_code",
      header: "사내품번",
      muiTableHeadCellProps: { align: "center" },
      muiTableBodyCellProps: { align: "center" },
    },
    {
      accessorKey: "material_grouping",
      header: "사내품명",
      muiTableHeadCellProps: { align: "center" },
      muiTableBodyCellProps: { align: "center" },
    },
    {
      accessorKey: "spec",
      header: "규격",
      muiTableHeadCellProps: { align: "center" },
      muiTableBodyCellProps: { align: "center" },
    },
    {
      accessorKey: "car_type",
      header: "차종",
      muiTableHeadCellProps: { align: "center" },
      muiTableBodyCellProps: { align: "center" },
    },
    {
      accessorKey: "item_code",
      header: "고객사 품번",
      muiTableHeadCellProps: { align: "center" },
      muiTableBodyCellProps: { align: "center" },
    },
    {
      accessorKey: "total_stock_quantity",
      header: "현재 재고",
      Cell: ({ cell }) => formatQuantity(cell.getValue()),
      muiTableHeadCellProps: { align: "center" },
      muiTableBodyCellProps: { align: "center" },
    },
    {
      accessorKey: "expected_stock",
      header: "예정 재고",
      Cell: ({ cell }) => formatQuantity(cell.getValue()),
      muiTableHeadCellProps: { align: "center" },
      muiTableBodyCellProps: { align: "center" },
    },
    {
      accessorKey: "min_stock_quantity",
      header: "안전 재고",
      Cell: ({ cell }) => formatQuantity(cell.getValue()),
      muiTableHeadCellProps: { align: "center" },
      muiTableBodyCellProps: { align: "center" },
    },
    {
      id: "change",
      header: "차이량",
      Cell: ({ row }) => {
        const change =
          Number(row.original.total_stock_quantity) +
          Number(row.original.expected_stock) -
          Number(row.original.min_stock_quantity);
        return renderChangeIndicator(change);
      },
      accessorFn: (row) =>
        Number(row.total_stock_quantity) +
        Number(row.expected_stock) -
        Number(row.min_stock_quantity),
      sortingFn: "basic",
      muiTableHeadCellProps: { align: "center" },
      muiTableBodyCellProps: { align: "center" },
    },
    {
      id: "status",
      header: "상태",
      Cell: ({ row }) => {
        const isLow =
          Number(row.original.total_stock_quantity) +
            Number(row.original.expected_stock) <
          Number(row.original.min_stock_quantity);
        return (
          <Box
            sx={{
              backgroundColor: isLow ? "#fff4f4" : "#eaf7ea",
              color: isLow ? "#d32f2f" : "#2e7d32",
              borderRadius: "4px",
              padding: "4px 8px",
              display: "inline-block",
              fontWeight: "bold",
              fontSize: "0.75rem",
              textAlign: "center",
            }}
          >
            {isLow ? "부족" : "정상"}
          </Box>
        );
      },
      muiTableHeadCellProps: { align: "center" },
      muiTableBodyCellProps: { align: "center" },
    },
  ];

  const groupedStockByMaterial = useMemo(() => {
    const groupMap = new Map();
    filteredStockData.forEach((item) => {
      const key = item.material_grouping || item.item_code;
      if (!groupMap.has(key)) {
        groupMap.set(key, {
          material_grouping: key,
          total_stock_quantity: 0,
          min_stock_quantity: 0,
        });
      }
      const entry = groupMap.get(key);
      entry.total_stock_quantity += Number(item.total_stock_quantity);
      entry.min_stock_quantity += Number(item.min_stock_quantity);
    });
    return Array.from(groupMap.values());
  }, [filteredStockData]);

  const { stockedGroups, emptyGroups } = useMemo(() => {
    const stocked = [];
    const empty = [];
    groupedStockByMaterial.forEach((item) => {
      const qty = Number(item.total_stock_quantity);
      if (qty > 0) stocked.push(item);
      else empty.push(item);
    });
    return { stockedGroups: stocked, emptyGroups: empty };
  }, [groupedStockByMaterial]);

  const getTreemapOption = (title, groups) => {
    if (!groups || groups.length === 0) {
      return {
        title: {
          text: `${title}\n(데이터 없음)`,
          left: "center",
          top: "center",
          textStyle: { fontSize: 14, color: "#999" },
        },
      };
    }

    return {
      title: {
        text: title,
        left: "center",
        top: 10,
        textStyle: { fontSize: 14 },
      },
      tooltip: {
        formatter: (info) => {
          const { name, value } = info.data;
          return `${name}<br/>재고: ${value.toLocaleString()} EA`;
        },
      },
      series: [
        {
          type: "treemap",
          roam: false,
          nodeClick: false,
          breadcrumb: { show: false },
          label: {
            show: true,
            formatter: "{b}",
            overflow: "truncate",
          },
          upperLabel: {
            show: false,
          },
          itemStyle: {
            borderColor: "#fff",
            borderWidth: 1,
          },
          data: groups.map((g) => ({
            name: g.material_grouping,
            value: Number(g.total_stock_quantity),
          })),
        },
      ],
    };
  };

  const treemapStockedOption = useMemo(
    () => getTreemapOption("재고 있는 품목", stockedGroups),
    [stockedGroups]
  );

  const treemapEmptyOption = useMemo(
    () => getTreemapOption("재고 없는 품목", emptyGroups),
    [emptyGroups]
  );

  const barChartOptions = useMemo(() => {
    const sortedData = [...groupedStockByMaterial].sort((a, b) => {
      const nameA = (a.material_grouping || "").toLowerCase();
      const nameB = (b.material_grouping || "").toLowerCase();
      return nameA.localeCompare(nameB);
    });
  
    return {
      tooltip: {
        trigger: "axis",
        axisPointer: {
          type: "shadow",
        },
      },
      grid: {
        left: "3%",
        right: "4%",
        bottom: "10%",
        top: "8%",
        containLabel: true,
      },
      xAxis: {
        type: "value",
        name: "수량 (EA)",
        nameLocation: "middle",
        nameGap: 30,
      },
      yAxis: {
        type: "category",
        data: sortedData.map((item) => item.material_grouping),
        axisLabel: {
          interval: 0,
          rotate: 0,
        },
      },
      series: [
        {
          name: "재고량",
          type: "bar",
          data: sortedData.map((item) => Number(item.total_stock_quantity)),
          itemStyle: {
            color: function (params) {
              const item = sortedData[params.dataIndex];
              const currentStock = Number(item.total_stock_quantity);
              const safetyStock = Number(item.min_stock_quantity);
              
              // ✅ 100% 미만: #ff8787, 100% 이상: #52c41a
              return currentStock < safetyStock ? "#ff8787" : "#52c41a";
            },
          },
          label: {
            show: true,
            position: "right",
            formatter: "{c} EA",
          },
        },
      ],
    };
  }, [groupedStockByMaterial]);
  
  const safetyStockComparisonOptions = useMemo(() => {
    const sortedData = [...groupedStockByMaterial].sort((a, b) => {
      const nameA = (a.material_grouping || "").toLowerCase();
      const nameB = (b.material_grouping || "").toLowerCase();
      return nameA.localeCompare(nameB);
    });
  
    const chartData = sortedData.map((item) => {
      const currentStock = Number(item.total_stock_quantity);
      const safetyStock = Number(item.min_stock_quantity);
      const percentage = safetyStock === 0 ? 0 : (currentStock / safetyStock) * 100;
      
      return {
        name: item.material_grouping,
        currentStock,
        safetyStock,
        value: percentage - 100,
        actualPercentage: percentage,
      };
    });
  
    // ✅ 최대 절대값 계산 (중앙 고정을 위해)
    const maxAbsValue = Math.max(
      ...chartData.map((d) => Math.abs(d.value)),
      50 // 최소 범위 50% (100% 기준으로 50~150%)
    );
  
    return {
      tooltip: {
        trigger: "axis",
        axisPointer: {
          type: "shadow",
        },
        formatter: (params) => {
          const dataIndex = params[0].dataIndex;
          const data = chartData[dataIndex];
          return `
            <strong>${data.name}</strong><br/>
            현재 재고: ${data.currentStock.toLocaleString()} EA<br/>
            안전 재고: ${data.safetyStock.toLocaleString()} EA<br/>
            비율: ${data.actualPercentage.toFixed(1)}%
          `;
        },
      },
      grid: {
        left: "20%",
        right: "10%",
        bottom: "10%",
        top: "8%",
        containLabel: false,
      },
      xAxis: {
        type: "value",
        name: "안전재고 대비",
        nameLocation: "middle",
        nameGap: 30,
        min: -maxAbsValue,
        max: maxAbsValue,
        axisLabel: {
          formatter: (value) => {
            const actualValue = value + 100;
            return `${actualValue}%`;
          },
        },
        splitLine: {
          show: true,
          lineStyle: {
            type: "dashed",
            color: "#e0e0e0",
          },
        },
        axisLine: {
          show: true,
          onZero: true,
          lineStyle: {
            color: "#333",
            width: 2,
          },
        },
      },
      yAxis: {
        type: "category",
        data: chartData.map((item) => item.name),
        axisLabel: {
          interval: 0,
          rotate: 0,
          fontSize: 11,
        },
      },
      series: [
        {
          name: "재고 비율",
          type: "bar",
          data: chartData.map((item) => item.value),
          itemStyle: {
            color: (params) => {
              const percentage = chartData[params.dataIndex].actualPercentage;
              // ✅ 100% 미만: #ff8787, 100% 이상: #52c41a (초록색)
              return percentage < 100 ? "#ff8787" : "#52c41a";
            },
          },
          label: {
            show: true,
            position: (params) => {
              return params.value >= 0 ? "right" : "left";
            },
            formatter: (params) => {
              const percentage = chartData[params.dataIndex].actualPercentage;
              return `${percentage.toFixed(1)}%`;
            },
            fontSize: 11,
            fontWeight: "bold",
            color: "#333",
          },
          markLine: {
            silent: true,
            symbol: "none",
            data: [
              {
                xAxis: 0, // 100% (항상 중앙)
                lineStyle: {
                  type: "solid",
                  color: "#333",
                  width: 3,
                },
                label: {
                  show: true,
                  position: "insideEndTop",
                  formatter: "100%",
                  fontSize: 12,
                  fontWeight: "bold",
                  color: "#333",
                },
              },
            ],
          },
        },
      ],
    };
  }, [groupedStockByMaterial]);
  

  const pieChartOptions = useMemo(() => {
    const data = groupedStockByMaterial;
    return {
      tooltip: {
        trigger: "item",
        formatter: "{a} <br/>{b}: {c} EA ({d}%)",
      },
      legend: {
        type: "scroll",
        orient: "vertical",
        right: 10,
        top: "center",
        padding: [0, 20],
      },
      series: [
        {
          name: "재고 비율",
          type: "pie",
          radius: ["40%", "70%"],
          center: ["40%", "50%"],
          avoidLabelOverlap: false,
          itemStyle: {
            borderRadius: 8,
            borderColor: "#fff",
            borderWidth: 2,
          },
          label: {
            show: false,
          },
          emphasis: {
            label: {
              show: true,
              fontSize: 14,
              fontWeight: "bold",
            },
          },
          data: data.map((item) => ({
            value: Number(item.total_stock_quantity),
            name: item.material_grouping,
          })),
        },
      ],
    };
  }, [groupedStockByMaterial]);

  const getInStockTrendOptions = () => {
    const allowedItemCodes = getAllowedItemCodes();
    const { dates, series } = organizeTrendData(
      inStockTrendData,
      "in",
      allowedItemCodes,
      itemCodeToGroupMap
    );
    if (dates.length === 0 || series.length === 0) {
      return {
        title: {
          text: "데이터가 없습니다",
          left: "center",
          top: "center",
        },
      };
    }
    return {
      tooltip: { trigger: "axis" },
      legend: {
        type: "scroll",
        data: series.map((item) => item.name),
        bottom: 10,
      },
      grid: {
        left: "3%",
        right: "3%",
        bottom: "15%",
        top: "8%",
        containLabel: true,
      },
      xAxis: {
        type: "category",
        boundaryGap: false,
        data: dates.map((date) => {
          const dateObj = new Date(date);
          return `${dateObj.getMonth() + 1}월 ${dateObj.getDate()}일`;
        }),
      },
      yAxis: {
        type: "value",
        name: "수량 (EA)",
      },
      series,
    };
  };

  const getOutStockTrendOptions = () => {
    const allowedItemCodes = getAllowedItemCodes();
    const { dates, series } = organizeTrendData(
      outStockTrendData,
      "out",
      allowedItemCodes,
      itemCodeToGroupMap
    );
    if (dates.length === 0 || series.length === 0) {
      return {
        title: {
          text: "데이터가 없습니다",
          left: "center",
          top: "center",
        },
      };
    }
    return {
      tooltip: { trigger: "axis" },
      legend: {
        type: "scroll",
        data: series.map((item) => item.name),
        bottom: 10,
      },
      grid: {
        left: "3%",
        right: "3%",
        bottom: "15%",
        top: "8%",
        containLabel: true,
      },
      xAxis: {
        type: "category",
        boundaryGap: false,
        data: dates.map((date) => {
          const dateObj = new Date(date);
          return `${dateObj.getMonth() + 1}월 ${dateObj.getDate()}일`;
        }),
      },
      yAxis: {
        type: "value",
        name: "수량 (EA)",
      },
      series,
    };
  };

  const getStockQuantityTrendOptions = () => {
    const allowedItemCodes = getAllowedItemCodes();
    const { dates, series } = organizeTrendData(
      stockQuantityTrendData,
      "stock",
      allowedItemCodes,
      itemCodeToGroupMap
    );
    if (dates.length === 0 || series.length === 0) {
      return {
        title: {
          text: "데이터가 없습니다",
          left: "center",
          top: "center",
        },
      };
    }
    return {
      tooltip: { trigger: "axis" },
      legend: {
        type: "scroll",
        data: series.map((item) => item.name),
        bottom: 10,
      },
      grid: {
        left: "3%",
        right: "3%",
        bottom: "15%",
        top: "8%",
        containLabel: true,
      },
      xAxis: {
        type: "category",
        boundaryGap: false,
        data: dates.map((date) => {
          const dateObj = new Date(date);
          return `${dateObj.getMonth() + 1}월 ${dateObj.getDate()}일`;
        }),
      },
      yAxis: {
        type: "value",
        name: "수량 (EA)",
      },
      series,
    };
  };

  const inStockOptions = useMemo(
    () => getInStockTrendOptions(),
    [inStockTrendData, itemTypeFilter, itemCodeToGroupMap]
  );

  const outStockOptions = useMemo(
    () => getOutStockTrendOptions(),
    [outStockTrendData, itemTypeFilter, itemCodeToGroupMap]
  );

  const stockQtyOptions = useMemo(
    () => getStockQuantityTrendOptions(),
    [stockQuantityTrendData, itemTypeFilter, itemCodeToGroupMap]
  );

  return (
    <Wrapper>
      <FrameContainer>
        <ToolbarContainer>
          <ToolbarLeft>
            <Typography
              variant="h5"
              fontWeight={700}
              sx={{ fontSize: "22px !important", color: "#111" }}
            >
              재고 현황
            </Typography>
          </ToolbarLeft>
          <ToolbarRight>
            <FormControl size="small" sx={{ minWidth: 140 }}>
              <InputLabel id="item-type-filter-label">품목 구분</InputLabel>
              <Select
                labelId="item-type-filter-label"
                value={itemTypeFilter}
                label="품목 구분"
                onChange={handleItemTypeFilterChange}
              >
                <MenuItem value="all">전체</MenuItem>
                <MenuItem value="finished">완제품</MenuItem>
                <MenuItem value="semi">반제품</MenuItem>
              </Select>
            </FormControl>
          </ToolbarRight>
        </ToolbarContainer>
        <DashboardContainer>
          {loading ? (
            <Box
              display="flex"
              justifyContent="center"
              alignItems="center"
              height="100%"
            >
              <CircularProgress />
            </Box>
          ) : (
            <Grid container spacing={2}>
              <Grid item xs={12} container spacing={2}>
                <Grid item xs={12} md={4}>
                  <StatCard>
                    <StatCardContent>
                      <Typography color="textSecondary" gutterBottom>
                        총 품명 수
                      </Typography>
                      <Typography variant="h4" component="div">
                        {filteredStockData.length}
                      </Typography>
                      <Typography variant="body2" color="textSecondary">
                        등록된 전체 품명
                      </Typography>
                    </StatCardContent>
                  </StatCard>
                </Grid>
                <Grid item xs={12} md={4}>
                  <StatCard>
                    <StatCardContent>
                      <Typography color="textSecondary" gutterBottom>
                        총 재고량
                      </Typography>
                      <Typography variant="h4" component="div">
                        {formatQuantity(calculateTotalStock())}
                      </Typography>
                      <Typography variant="body2" color="textSecondary">
                        모든 품명의 재고 합계
                      </Typography>
                    </StatCardContent>
                  </StatCard>
                </Grid>
                <Grid item xs={12} md={4}>
                  <StatCard
                    sx={{
                      bgcolor: lowStockItems.length > 0 ? "#fff9f9" : "#f5fff5",
                    }}
                  >
                    <StatCardContent>
                      <Typography color="textSecondary" gutterBottom>
                        재고 부족 품명
                      </Typography>
                      <Box display="flex" alignItems="center">
                        <Typography
                          variant="h4"
                          component="div"
                          color={lowStockItems.length > 0 ? "error" : "success"}
                        >
                          {lowStockItems.length}
                        </Typography>
                        {lowStockItems.length > 0 && (
                          <WarningIcon color="error" sx={{ ml: 1 }} />
                        )}
                      </Box>
                      <Typography
                        variant="body2"
                        color={lowStockItems.length > 0 ? "error" : "success"}
                      >
                        {lowStockItems.length > 0
                          ? "생산이 필요한 품명"
                          : "모든 품명 정상 재고"}
                      </Typography>
                    </StatCardContent>
                  </StatCard>
                </Grid>
              </Grid>
              <Grid item xs={12}>
                <Paper sx={{ p: 2 }}>
                  <Typography variant="h6" mb={2}>
                    재고 상세 현황
                  </Typography>
                  <MaterialReactTable
                    columns={columns}
                    data={filteredStockData}
                    enableColumnActions={false}
                    enableColumnFilters={false}
                    enableSorting
                    enableTopToolbar={false}
                    enablePagination
                    enableBottomToolbar
                    autoResetPageIndex={true}
                    initialState={{
                      density: "compact",
                      pagination: { pageSize: 10, pageIndex: 0 },
                    }}
                  />
                </Paper>
              </Grid>

              {/* ✅ 현 재고 구성 현황 - 3개 차트 */}
              <Grid item xs={12}>
                <ChartContainer>
                  <ChartHeader>
                    <Typography variant="h6">현 재고 구성 현황</Typography>
                  </ChartHeader>
                  <Divider sx={{ mb: 2 }} />
                  <TripleChartContainer>
                    {/* 1. 재고량 바 차트 */}
                    <ChartSection>
                      <ReactECharts
                        option={barChartOptions}
                        style={{ height: "100%", width: "100%" }}
                      />
                    </ChartSection>

                    {/* 2. 안전재고 대비 비율 차트 */}
                    <ChartSection>
                      <ReactECharts
                        option={safetyStockComparisonOptions}
                        style={{ height: "100%", width: "100%" }}
                      />
                    </ChartSection>

                    {/* 3. 트리맵 차트 */}
                    <ChartSection>
                      {emptyGroups.length === 0 ? (
                        <ReactECharts
                          option={treemapStockedOption}
                          style={{ height: "100%", width: "100%" }}
                        />
                      ) : (
                        <Box
                          sx={{
                            display: "flex",
                            flexDirection: "column",
                            height: "100%",
                          }}
                        >
                          <Box sx={{ flex: 1, mb: 1 }}>
                            <ReactECharts
                              option={treemapStockedOption}
                              style={{ height: "100%", width: "100%" }}
                            />
                          </Box>
                          <Box sx={{ flex: 1, mt: 1 }}>
                            <ReactECharts
                              option={treemapEmptyOption}
                              style={{ height: "100%", width: "100%" }}
                            />
                          </Box>
                        </Box>
                      )}
                    </ChartSection>
                  </TripleChartContainer>
                </ChartContainer>
              </Grid>
            </Grid>
          )}
        </DashboardContainer>
      </FrameContainer>
      <Snackbar
        open={snackbar.open}
        autoHideDuration={3000}
        onClose={closeSnackbar}
        anchorOrigin={{ vertical: "bottom", horizontal: "center" }}
      >
        <Alert
          onClose={closeSnackbar}
          severity={snackbar.severity}
          variant="filled"
          sx={{ width: "100%" }}
        >
          {snackbar.message}
        </Alert>
      </Snackbar>
      <StockInModal
        open={openInModal}
        onClose={() => setOpenInModal(false)}
        openSnackbar={openSnackbar}
        setUpdateStatus={setUpdateStatus}
      />
      <StockOutModal
        open={openOutModal}
        onClose={() => setOpenOutModal(false)}
        openSnackbar={openSnackbar}
        setUpdateStatus={setUpdateStatus}
      />
    </Wrapper>
  );
}

export default Stock;
